import { CreateBtnDescType, RenderToolbarHandler, ResizeOptionType } from '.';
import { FC, HtmlHTMLAttributes, ReactNode, useEffect, useMemo } from 'react';
import { useToggle } from 'src/Hooks';
import clx from 'classnames';
import SubMenuContainer from './Container/SubMenuContainer';
import SubMenuFooter from './Footer/SubMenuFooter';
import SubmenuHeader from './Header/SubmenuHeader';
import st from './Submenu.module.scss';

interface ISubMenuProps extends HtmlHTMLAttributes<HTMLDivElement> {
	/** Заголовок */
	title?: string;
	/** Блокирует все действия панели */
	disabled?: boolean;
	/** Начальное состояния панели */
	isRenderHeader?: boolean;
	/** Начальное состояния панели */
	isRenderFooter?: boolean;
	/** Начальное состояния панели */
	defaultExpanded?: boolean;
	/** Флаг запрещающий сворачивать/разворачивать панель */
	disableExpand?: boolean;
	/** Строка Медиа-запроса, при срабатывании которого панель будет автоматически свернута */
	autoCollapseMedia?: string;
	/** Рендерить ли контейнер для кнопки создания */
	isRenderCreateButton?: boolean;
	/** Начальное состояния панели */
	createBtn?: CreateBtnDescType;
	/** Функция отрисовки контента */
	renderContent?: FC<ISubMenuRenderProps>;
	renderToolbar?: RenderToolbarHandler;
	/**
	 * Функция отрисовки футера панели
	 * (Будет рендерится Перед кнопкой сворачивания)
	 */
	renderFooter?: FC;
	onChangeExpand?: (newValue: boolean) => void;
	children?: ReactNode;
	resizeOption?: ResizeOptionType;
}

interface ISubMenuRenderProps extends ISubMenuProps {
	expanded: boolean;
}

/** Компонент-контейнер для отображения подменю */
const SubMenu: FC<ISubMenuProps> = (props) => {
	const {
		id = 'rdc_submenu',
		title = '',
		renderFooter,
		children,
		renderContent,
		createBtn,
		disabled = false,
		className,
		isRenderHeader = true,
		isRenderFooter = true,
		resizeOption,
		onChangeExpand,
		renderToolbar,
		defaultExpanded = false,
		disableExpand = false,
		...otherProps
	} = props;

	const [expanded, toggle] = useToggle(defaultExpanded);

	useEffect(() => {
		if (onChangeExpand) {
			onChangeExpand(expanded);
		}
		// eslint-disable-next-line
	}, [expanded]);

	const innerProps: ISubMenuRenderProps = useMemo(() => {
		return {
			expanded,
			...props,
			// eslint-disable-next-line
		};
	}, [expanded]);

	const renderBody = () => {
		if (!expanded) {
			return null;
		}
		return (
			<div className={clx(st.rdc_submenu__content, 'scrolled')}>
				{!!children && children}
				{!!renderContent && renderContent(innerProps)}
			</div>
		);
	};

	return (
		<SubMenuContainer
			// Пересоздание запускает анимацию и быстро сбрасывает подменю,
			// чтобы ячейки таблицы успели пересчитать ширину
			key={String(expanded)}
			id={id}
			expanded={expanded}
			disableResizing={disabled}
			resizeOptions={resizeOption}
			{...otherProps}
		>
			{isRenderHeader && (
				<SubmenuHeader
					id={id}
					expanded={expanded}
					disabled={disabled}
					title={title}
					renderToolbar={renderToolbar}
					{...props}
				/>
			)}
			{renderBody()}
			{isRenderFooter && (
				<SubMenuFooter
					id={id}
					expanded={expanded}
					toggleExpand={toggle}
					disabled={disableExpand || disabled}
					renderFooter={renderFooter}
				/>
			)}
		</SubMenuContainer>
	);
};

export default SubMenu;
